import { NAVIGATION_DEFAULT_LOCALE, TNavigationLocales } from 'src/types/locales'

export type AnyObject = {
  [key: string]: any
}

/**
 * Ensure all properties and nested objects' properties are localized in the
 * correct locale
 * This function is useful when the data isn't localized, for example, any message
 * coming from websockets needs to be translated on the client's device.
 *
 * Fields to be localized must start with `intl`. The localized field is named
 * accordingly, without `intl`. Example: `intlDescription` -> `description`
 *
 * @param {AnyObject} input
 * @param locale
 * @returns {AnyObject}
 */
export function localizeObject<T>(input: T, locale: TNavigationLocales): T {
  if (Array.isArray(input)) {
    // Recursively transform array elements
    return input.map((item) => localizeObject(item, locale)) as T
  } else if (typeof input === 'object' && input !== null && !(input instanceof Date)) {
    // Recursively transform nested objects
    const res = input as any
    const objectIntlFields = Object.keys(res).filter((key) => key.startsWith('intl'))

    for (const key of Object.keys(res)) {
      if (objectIntlFields.includes(key)) {
        const localizedFieldName = key.replace('intl', '').toLowerCase()
        res[localizedFieldName] = res[key]?.[locale] || res[key]?.[NAVIGATION_DEFAULT_LOCALE]
        continue
      }
      if (Object.hasOwn(res, key) && typeof res[key] === 'object') {
        res[key] = localizeObject(res[key], locale)
      }
    }
    return res
  }
  // Leave other values unchanged
  return input
}
